/**
 * Location utilities
 *
 * Purpose: small client-side helpers for working with geographic coordinates
 * used by the search experience. These helpers intentionally rely on the
 * browser's Google Maps JavaScript API (when geocoding) and the
 * Geolocation/Permissions APIs so behavior is consistent with the production
 * mapping stack.
 */
import distance from '@turf/distance'
import { point } from '@turf/helpers'

const SCMG_SERVICE_AREA_COUNTIES = new Set(['San Diego County'])

export type Coordinates = {
  lat: number
  lng: number
  label?: string
  location?: string
}

export type ResolvedLocation = Coordinates & {
  formattedAddress?: string
  geoData?: GeoData
}

export type GeoData = {
  id?: string
  lat?: number
  lng?: number
  sublocality?: string
  city?: string
  state?: string
  postalCode?: string
  county?: string
  country?: string
  formattedAddress?: string
  hasExplicitPostalCode?: boolean
}

function normalizeLocationLabel(label?: string | null): string {
  return label?.replace(/, USA$/i, '').trim() ?? ''
}

/**
 * Check whether coordinates fall inside SCMG's manually defined service-area box.
 */
export function isWithinScmgServiceArea(coordinates: Pick<Coordinates, 'lat' | 'lng'>): boolean {
  return (
    // Keep Sharp's Google Places flow unchanged and express SCMG-specific behavior
    // only through this shared autosuggest rectangle. It does not determine which
    // SCMG providers appear in search results, and should stay synchronized with
    // fallback validation instead of adding custom filtering logic.
    coordinates.lat >= 32.534 &&
    coordinates.lat <= 33.95 &&
    coordinates.lng >= -118.01 &&
    coordinates.lng <= -115
  )
}

/**
 * Accept California geo results that either match an allowed county or fall inside SCMG's fallback bounds.
 */
export function isScmgGeoData(location?: GeoData | null): boolean {
  if (!location?.lat || !location?.lng) {
    return false
  }

  const matchesCounty = location.county ? SCMG_SERVICE_AREA_COUNTIES.has(location.county) : false
  const matchesState = location.state === 'CA'

  return matchesState && (matchesCounty || isWithinScmgServiceArea({ lat: location.lat, lng: location.lng }))
}

/**
 * Request the user's current browser coordinates after an optional permissions check.
 */
export async function getBrowserLocation(): Promise<Coordinates | null> {
  if (globalThis.window === undefined || !globalThis.navigator?.geolocation) {
    return null
  }

  const browserNavigator = globalThis.navigator

  type GeolocationPermissionDescriptor = PermissionDescriptor & {
    name: 'geolocation'
  }

  try {
    const result = await browserNavigator.permissions?.query?.({
        name: 'geolocation',
      } as GeolocationPermissionDescriptor)

    if (result?.state === 'denied') {
      return null
    }
  } catch {
    // Ignore permission probe failures and attempt the geolocation request.
  }

  return new Promise((resolve) => {
    // This request only runs after the user explicitly chooses Current location.
    // eslint-disable-next-line sonarjs/no-intrusive-permissions
    browserNavigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      },
      (error) => {
        console.warn('[getBrowserLocation] Geolocation error:', error?.message)
        resolve(null)
      },
      {
        timeout: 10000,
        maximumAge: 300000,
        enableHighAccuracy: false,
      },
    )
  })
}

/**
 * Calculate the straight-line distance in miles between two coordinates using Turf.js.
 */
export function calculateDistance(from: Coordinates, to: Coordinates): number {
  const fromPoint = point([from.lng, from.lat])
  const toPoint = point([to.lng, to.lat])
  const distanceKm = distance(fromPoint, toPoint, { units: 'kilometers' })
  return distanceKm * 0.621371
}

/**
 * Format coordinates as the `lat,lng` string Algolia expects for `aroundLatLng`.
 */
export function formatAlgoliaLatLng(coords: Coordinates): string {
  return `${coords.lat},${coords.lng}`
}

/**
 * Build a user-facing location label from parsed geo parts using the app's display rules.
 */
export const formatLocationString = (location?: GeoData): string => {
  let result = ''

  if (location?.sublocality) {
    result += `${location.sublocality.replace(location.city ?? '', '').trim()}`
  }
  if (location?.city) {
    result += `${result.length > 0 ? ', ' : ''}${location.city}`
  }
  if (location?.state) {
    result += `${result.length > 0 ? ', ' : ''}${location.state}`
  }
  if (location?.postalCode && location.hasExplicitPostalCode) {
    result += `${result.length > 0 ? ' ' : ''}${location.postalCode}`
  }

  return result
}

/**
 * Extract the geo fields SCMG cares about from a Google geocoder result.
 */
export const parseGeocodeResult = (result: any): GeoData => {
  const geoData: GeoData = {}
  const addressComponents = result.address_components
  const lat = Number.parseFloat(result.geometry.location.lat())
  const lng = Number.parseFloat(result.geometry.location.lng())
  const id = result.place_id

  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
    geoData.lat = lat
    geoData.lng = lng
    geoData.id = id
    addressComponents?.forEach((element: any) => {
      if (element.types.includes('postal_code')) {
        geoData.postalCode = element.short_name
        geoData.hasExplicitPostalCode = true
      } else if (element.types.includes('neighborhood')) {
        geoData.sublocality = element.short_name
      } else if (element.types.includes('sublocality')) {
        geoData.sublocality = element.short_name
      } else if (element.types.includes('locality')) {
        geoData.city = element.short_name
      } else if (element.types.includes('administrative_area_level_1')) {
        geoData.state = element.short_name
      } else if (element.types.includes('administrative_area_level_2')) {
        geoData.county = element.long_name
      } else if (element.types.includes('country')) {
        geoData.country = element.short_name
      }
    })

    geoData.formattedAddress = normalizeLocationLabel(formatLocationString(geoData) || result.formatted_address)
  }

  return geoData
}
